import {useState, useMemo, useCallback, type RefObject} from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  Drawer,
  IconButton,
  Tooltip,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LandscapeIcon from '@mui/icons-material/Landscape'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import ImageIcon from '@mui/icons-material/Image'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import ViewInArIcon from '@mui/icons-material/ViewInAr'
import AspectRatioIcon from '@mui/icons-material/AspectRatio'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import {useTranslation} from 'react-i18next'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import type {RenderExportFn} from '../../../common/components/ExportHandler'
import {useAppSelector} from '../../../common/store'
import {selectActiveScene} from '../../../common/components/store/scenesSlice'
import {setExportFramePreview} from '../../../common/components/exportFramePreviewStore'
import {useProjectName} from '../../../common/components/store/projectNameStore'
import SceneSettingsPanel from '../leftSidebar/SceneSettingsPanel'
import LightingShadowPanel from '../leftSidebar/LightingShadowPanel'
import CameraPresetsPanel from '../leftSidebar/CameraPresetsPanel'
import DeviceSelectPanel from '../leftSidebar/DeviceSelectPanel'
import DeviceShapesPanel from '../leftSidebar/DeviceShapesPanel'
import DeviceFrontFacePanel from '../leftSidebar/DeviceFrontFacePanel'
import DeviceSideEdgesPanel from '../leftSidebar/DeviceSideEdgesPanel'
import DeviceMaterialsPanel from '../leftSidebar/DeviceMaterialsPanel'
import ScreenImagePanel from '../rightSidebar/ScreenImagePanel'
import Text2DPanel from '../rightSidebar/Text2DPanel'
import Text3DPanel from '../rightSidebar/Text3DPanel'
import ExportResolutionPanel from '../rightSidebar/ExportResolutionPanel'
import ExportDialog from '../rightSidebar/ExportDialog'
import useEnsureSavedProject from '../../hooks/useEnsureSavedProject'

// ── MobileEditorDrawer — single drawer combining all editor panels ──

interface Props {
  renderExportRef: RefObject<RenderExportFn | null>
  onExportRendering?: (rendering: boolean) => void
  workerBusy?: boolean
}

const MobileEditorDrawer = ({renderExportRef, onExportRendering, workerBusy}: Props) => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const projectName = useProjectName()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [expanded, setExpanded] = useState<string | false>('screenImage')
  const exportFrameEnabled = useAppSelector((s) => selectActiveScene(s).exportFrameEnabled)
  const {ensureSaved, dialog: saveDialog, isSaving} = useEnsureSavedProject()

  const handleOpenDrawer = useCallback(() => setDrawerOpen(true), [])
  const handleCloseDrawer = useCallback(() => setDrawerOpen(false), [])

  const handleOpenExport = useCallback(async () => {
    const ok = await ensureSaved()
    if (!ok) return
    setDrawerOpen(false)
    setExportDialogOpen(true)
  }, [ensureSaved])

  const handleCloseExport = useCallback(() => {
    setExportDialogOpen(false)
  }, [])

  const handleExportButtonEnter = useCallback(() => {
    if (!exportFrameEnabled) setExportFramePreview(true)
  }, [exportFrameEnabled])

  const handleExportButtonLeave = useCallback(() => {
    setExportFramePreview(false)
  }, [])

  const handleAccordionChange = (panel: string) => (_: unknown, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  const sx = useMemo(() => buildStyles(tokens), [tokens])
  const displayName = projectName ?? t('topBar.untitledProject')

  return (
    <>
      <Tooltip title={t('freeSidebar.openControls')} placement="right">
        <IconButton aria-label={t('freeSidebar.openControls')} onClick={handleOpenDrawer} sx={sx.burger}>
          <MenuRoundedIcon sx={sx.burgerIcon} />
        </IconButton>
      </Tooltip>
      <Drawer anchor="left" open={drawerOpen} onClose={handleCloseDrawer} slotProps={{paper: {sx: sx.drawerPaper}}}>
        <Box sx={sx.panel}>
          <Box sx={sx.header}>
            <Typography sx={sx.headerName}>{displayName}</Typography>
          </Box>

          <Box sx={sx.scrollArea}>
            {/* Screen Image (first, default open) */}
            <Accordion
              expanded={expanded === 'screenImage'}
              onChange={handleAccordionChange('screenImage')}
              disableGutters
              square
              sx={sx.accordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.summary}>
                <ImageIcon sx={sx.sectionIcon} />
                <Typography sx={sx.sectionTitle}>{t('leftSidebar.screenImage')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={sx.details}>
                <ScreenImagePanel />
              </AccordionDetails>
            </Accordion>

            {/* Scene */}
            <Accordion
              expanded={expanded === 'scene'}
              onChange={handleAccordionChange('scene')}
              disableGutters
              square
              sx={sx.accordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.summary}>
                <LandscapeIcon sx={sx.sectionIcon} />
                <Typography sx={sx.sectionTitle}>{t('sidebar.scene')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={sx.details}>
                <SceneSettingsPanel />
                <LightingShadowPanel />
                <CameraPresetsPanel />
              </AccordionDetails>
            </Accordion>

            {/* 2D Text */}
            <Accordion
              expanded={expanded === 'text2d'}
              onChange={handleAccordionChange('text2d')}
              disableGutters
              square
              sx={sx.accordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.summary}>
                <TextFieldsIcon sx={sx.sectionIcon} />
                <Typography sx={sx.sectionTitle}>{t('text2d.title')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={sx.details}>
                <Text2DPanel />
              </AccordionDetails>
            </Accordion>

            {/* 3D Text */}
            <Accordion
              expanded={expanded === 'text3d'}
              onChange={handleAccordionChange('text3d')}
              disableGutters
              square
              sx={sx.accordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.summary}>
                <ViewInArIcon sx={sx.sectionIcon} />
                <Typography sx={sx.sectionTitle}>{t('text3d.title')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={sx.details}>
                <Text3DPanel />
              </AccordionDetails>
            </Accordion>

            {/* Export resolution */}
            <Accordion
              expanded={expanded === 'export'}
              onChange={handleAccordionChange('export')}
              disableGutters
              square
              sx={sx.accordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.summary}>
                <AspectRatioIcon sx={sx.sectionIcon} />
                <Typography sx={sx.sectionTitle}>{t('exportResolution.title')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={sx.details}>
                <ExportResolutionPanel />
              </AccordionDetails>
            </Accordion>

            {/* Device (last) */}
            <Accordion
              expanded={expanded === 'device'}
              onChange={handleAccordionChange('device')}
              disableGutters
              square
              sx={sx.accordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.summary}>
                <PhoneIphoneIcon sx={sx.sectionIcon} />
                <Typography sx={sx.sectionTitle}>{t('sidebar.device')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={sx.details}>
                <DeviceSelectPanel />
                <DeviceShapesPanel />
                <DeviceFrontFacePanel />
                <DeviceSideEdgesPanel />
                <DeviceMaterialsPanel />
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Export button pinned at bottom */}
          <Box sx={sx.exportSection}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<FileDownloadIcon />}
              onClick={handleOpenExport}
              onMouseEnter={handleExportButtonEnter}
              onMouseLeave={handleExportButtonLeave}
              disabled={workerBusy || isSaving}
              sx={{fontSize: 12, textTransform: 'none'}}
            >
              {t('exportDialog.title')}
            </Button>
          </Box>
        </Box>
      </Drawer>
      {saveDialog}
      <ExportDialog
        open={exportDialogOpen}
        onClose={handleCloseExport}
        renderExportRef={renderExportRef}
        onRenderingChange={onExportRendering}
      />
    </>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  burger: {
    position: 'absolute' as const,
    bottom: 'max(16px, calc(env(safe-area-inset-bottom) + 8px))',
    left: 16,
    zIndex: 25,
    width: 48,
    height: 48,
    borderRadius: '12px',
    color: '#ffffff',
    backgroundColor: tokens.accent.primary,
    border: `1px solid ${tokens.accent.dark}`,
    boxShadow: `0 4px 16px ${tokens.accent.glow}`,
    '&:hover': {
      backgroundColor: tokens.accent.light,
      borderColor: tokens.accent.primary,
    },
  },
  burgerIcon: {
    fontSize: 22,
  },
  drawerPaper: {
    width: tokens.sidebar.width,
    backgroundColor: tokens.sidebar.background,
    backgroundImage: 'none',
  },
  panel: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  header: {
    padding: '14px 16px',
    borderBottom: `1px solid ${tokens.accordion.border}`,
    flexShrink: 0,
  },
  headerName: {
    fontSize: 14,
    fontWeight: 600,
    color: tokens.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto' as const,
  },
  accordion: {
    backgroundColor: 'transparent',
    color: tokens.text.primary,
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  summary: {
    borderBottom: `1px solid ${tokens.accordion.border}`,
    minHeight: 48,
    '&.Mui-expanded': {minHeight: 48},
    '& .MuiAccordionSummary-content': {
      margin: '12px 0',
      alignItems: 'center',
      gap: '8px',
      '&.Mui-expanded': {margin: '12px 0'},
    },
  },
  expandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 20,
  },
  sectionIcon: {
    color: tokens.accordion.sectionIcon,
    fontSize: 18,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: tokens.text.primary,
  },
  details: {
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.accordion.border}`,
  },
  exportSection: {
    borderTop: `1px solid ${tokens.accordion.border}`,
    padding: '12px 16px',
    flexShrink: 0,
    backgroundColor: tokens.sidebar.background,
  },
})

export default MobileEditorDrawer
